import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { detectEink, refineEinkViaClientHints, applyPagedTransformFlag } from './lib/eink';
import { detectInAppBrowser } from './lib/inAppBrowser';
import InAppBrowserGate from './components/InAppBrowserGate';
import { resetDbState } from './lib/db/helpers';
import './styles/global.css';
import './styles/eink.css';

// iOS Safari bfcache freezes the SPA's JS heap; on Back-button restore the
// stale module state — including a stuck `dbFailed=true` from an earlier
// IDB error — comes back to life. Without this reset, every IDB read returns
// empty/null and Library, Reader, Sidebar all silently fail to load. The
// listener is installed once at module init (before the AuthProvider tree
// mounts) so it's wired up the moment bfcache restoration fires pageshow.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) resetDbState();
});

// In-app browser gate must run BEFORE any of Reader's heavy initialisers
// (AuthProvider / IDB open / sync) so we never invoke the IDB code paths
// that would otherwise silently corrupt or loop in WeChat/QQ/Weibo WebViews.
// See shejiwushi@qq.com incident (2026-04-22) — 7 IDB transaction errors
// in 11 minutes inside MicroMessenger/8.0.71 before the user gave up.
const inApp = detectInAppBrowser();

if (inApp.isInApp) {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <InAppBrowserGate appName={inApp.name} />
    </React.StrictMode>
  );
} else {
  // E-ink display detection happens once at module load — physical screen
  // properties don't change at runtime, so no listener is needed. Library /
  // Auth pages share the <html data-eink> attribute with Reader.
  document.documentElement.setAttribute('data-eink', String(detectEink()));
  applyPagedTransformFlag();
  // Async second pass: UA Reduction (Chrome 110+) hides the device model
  // from the sync UA sniff; Client Hints still expose it. Flips the
  // attribute + persists on match, so the NEXT load is sync-correct too.
  refineEinkViaClientHints();

  // One-shot cleanup of legacy localStorage keys left behind by removed features.
  // Safe to remove this block after ~6 months (by which most browsers will have cleaned).
  try {
    const CLEANUP_FLAG = 'ev_legacy_cleanup_v2';
    if (!localStorage.getItem(CLEANUP_FLAG)) {
      localStorage.removeItem('chunk_upgrade_dismissed'); // old chunksUpgradeAvailable banner dismiss state
      localStorage.removeItem('easeverse_reader_eink_mode'); // user-facing e-ink toggle removed; detect-only now
      localStorage.setItem(CLEANUP_FLAG, '1');
    }
  } catch { /* localStorage unavailable — private mode, SSR, etc. */ }

  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <AuthProvider>
        <App />
      </AuthProvider>
    </React.StrictMode>
  );
}

